/**
 * Right-click context menus.
 *
 * Two entry points:
 *   - `mountContextMenu(canvas)` listens for `mapnmove:token-context` and
 *     `mapnmove:overlay-context` CustomEvents fired by canvas.ts and shows
 *     the appropriate menu (token actions, or wall/door actions).
 *   - `openTokenDeleteMenu(token, x, y)` is exported for the legend +
 *     initiative panel; it shows a single-item "Delete token" menu (fully
 *     deletes the token, no confirm).
 *
 * Menu DOM, dismiss-on-outside-click + ESC, and viewport clamping all live
 * at module level so both entry points share one menu at a time.
 */

import * as api from './api.js';
import type { OverlayContextEventDetail, TokenContextEventDetail } from './canvas.js';
import { getState, removeToken, updateOverlayCell, upsertToken } from './state.js';
import type { OverlayCell, Token } from './types.js';

interface MenuController {
  destroy: () => void;
}

// ---------------------------------------------------------------------------
// Module-level menu state — only one menu is open at a time, regardless of
// which entry point opened it.
// ---------------------------------------------------------------------------

let activeMenu: HTMLElement | null = null;

function dismiss(): void {
  if (activeMenu === null) return;
  activeMenu.remove();
  activeMenu = null;
  document.removeEventListener('mousedown', onOutsideMouseDown);
  document.removeEventListener('keydown', onEscape);
}

function onOutsideMouseDown(e: MouseEvent): void {
  if (activeMenu === null) return;
  if (e.target instanceof Node && activeMenu.contains(e.target)) return;
  dismiss();
}

function onEscape(e: KeyboardEvent): void {
  if (e.key === 'Escape') dismiss();
}

interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

/**
 * Build and show a menu at (clientX, clientY). Replaces any open menu.
 * Returns the new menu element (null if `items` was empty).
 */
function presentMenu(items: MenuItem[], clientX: number, clientY: number): HTMLElement | null {
  dismiss();
  if (items.length === 0) return null;

  const menu = document.createElement('div');
  menu.className = 'context-menu';
  menu.setAttribute('role', 'menu');
  menu.style.left = `${clientX}px`;
  menu.style.top = `${clientY}px`;

  for (const item of items) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'context-menu-item';
    if (item.danger === true) btn.classList.add('is-danger');
    btn.textContent = item.label;
    btn.setAttribute('role', 'menuitem');
    btn.addEventListener('click', () => {
      item.onClick();
      dismiss();
    });
    menu.appendChild(btn);
  }

  document.body.appendChild(menu);
  activeMenu = menu;

  clampIntoViewport(menu);

  // Defer dismiss handlers so the originating mouseup doesn't close us instantly.
  setTimeout(() => {
    document.addEventListener('mousedown', onOutsideMouseDown);
    document.addEventListener('keydown', onEscape);
  }, 0);

  return menu;
}

function clampIntoViewport(menu: HTMLElement): void {
  const rect = menu.getBoundingClientRect();
  if (rect.right > window.innerWidth) {
    menu.style.left = `${Math.max(0, window.innerWidth - rect.width - 4)}px`;
  }
  if (rect.bottom > window.innerHeight) {
    menu.style.top = `${Math.max(0, window.innerHeight - rect.height - 4)}px`;
  }
}

function alertError(err: unknown): void {
  console.error(err);
  if (err instanceof Error) {
    alert(err.message);
  } else {
    alert('Operation failed');
  }
}

// ---------------------------------------------------------------------------
// Canvas-mounted menus (token / overlay).
// ---------------------------------------------------------------------------

export function mountContextMenu(canvas: HTMLCanvasElement): MenuController {
  const onContext = (e: Event): void => {
    const detail = (e as CustomEvent<TokenContextEventDetail>).detail;
    showCanvasTokenMenu(detail.token, detail.clientX, detail.clientY);
  };

  const onOverlayContext = (e: Event): void => {
    const detail = (e as CustomEvent<OverlayContextEventDetail>).detail;
    showOverlayMenu(detail);
  };

  canvas.addEventListener('mapnmove:token-context', onContext as EventListener);
  canvas.addEventListener('mapnmove:overlay-context', onOverlayContext as EventListener);

  return {
    destroy(): void {
      dismiss();
      canvas.removeEventListener('mapnmove:token-context', onContext as EventListener);
      canvas.removeEventListener('mapnmove:overlay-context', onOverlayContext as EventListener);
    },
  };
}

function showCanvasTokenMenu(token: Token, clientX: number, clientY: number): void {
  presentMenu(
    [
      {
        label: 'Set status…',
        onClick: () => {
          // window.prompt() per spec. Empty string clears.
          const current = token.status_text ?? '';
          const next = window.prompt('Status text (empty to clear):', current);
          if (next === null) return;
          api.updateToken(token.id, { status_text: next })
            .then((updated) => upsertToken(updated))
            .catch((err) => alertError(err));
        },
      },
      {
        label: token.is_downed ? 'Mark up' : 'Mark downed',
        onClick: () => {
          api.updateToken(token.id, { is_downed: !token.is_downed })
            .then((updated) => upsertToken(updated))
            .catch((err) => alertError(err));
        },
      },
      {
        // "Remove from map" unplaces the token — it survives in the legend
        // and can be dragged back onto the canvas later. Distinct from the
        // panel's "Delete token", which fully deletes.
        label: 'Remove from map',
        onClick: () => {
          api.updateToken(token.id, { is_placed: false })
            .then((updated) => upsertToken(updated))
            .catch((err) => alertError(err));
        },
      },
    ],
    clientX,
    clientY,
  );
}

function showOverlayMenu(detail: OverlayContextEventDetail): void {
  const cell = detail.cell;
  if (cell === null) return;

  const setCellViaMenu = (next: OverlayCell | null): void => {
    updateOverlayCell(detail.gridX, detail.gridY, next);
  };

  // In token mode the only sensible action is opening/closing doors; wall
  // manipulation is gated to overlay-edit mode so a stray click can't
  // reshape the map mid-encounter.
  const isEditMode = getState().editMode === 'overlay';
  const items: MenuItem[] = [];

  if (cell.kind === 'solid') {
    if (!isEditMode) return; // walls aren't actionable in token mode
    items.push({ label: 'Add door', onClick: () => setCellViaMenu({ kind: 'door', open: false }) });
    items.push({ label: 'Remove wall', onClick: () => setCellViaMenu(null), danger: true });
  } else if (cell.kind === 'door') {
    items.push({
      label: cell.open ? 'Close door' : 'Open door',
      onClick: () => setCellViaMenu({ kind: 'door', open: !cell.open }),
    });
    if (isEditMode) {
      items.push({ label: 'Convert to wall', onClick: () => setCellViaMenu({ kind: 'solid' }) });
      items.push({ label: 'Remove door', onClick: () => setCellViaMenu(null), danger: true });
    }
  } else if (cell.kind === 'terrain') {
    if (!isEditMode) return;
    items.push({ label: 'Remove terrain', onClick: () => setCellViaMenu(null), danger: true });
  }

  presentMenu(items, detail.clientX, detail.clientY);
}

// ---------------------------------------------------------------------------
// Panel-mounted menu — used by legend.ts and initiativePanel.ts.
// ---------------------------------------------------------------------------

/**
 * Open the legend / initiative panel right-click menu for a token.
 *
 * Single-item destructive menu: clicking "Delete token" issues
 * `DELETE /api/tokens/:id` and removes the token from state. No confirm
 * dialog. If an encounter is active, `removeToken` reconciles the order
 * automatically.
 */
export function openTokenDeleteMenu(token: Token, clientX: number, clientY: number): void {
  presentMenu(
    [
      {
        label: 'Delete token',
        danger: true,
        onClick: () => {
          api.deleteToken(token.id)
            .then(() => removeToken(token.id))
            .catch((err) => alertError(err));
        },
      },
    ],
    clientX,
    clientY,
  );
}
